import { useMutation, useQuery } from '@tanstack/react-query';
import { Store } from 'sunflower-antd';
import { httpClient, ROOT_URL } from '../App';

const DEFAULT_BANK_ACCOUNT_PAGE_SIZE = 10;

export const useGetListBankAccount = (params: {
  companyId?: number
  start: number
  end: number
  name?: string
  currencyId?: number
  enabled?: boolean
}) => {
  return useQuery<{
    data: any[]
    totalCount: number
    page: number
    pageSize: number
    totalPages: number
  }>(
    [
      `/api/v1/bank_accounts/non_default`,
      params.companyId,
      params.start,
      params.end,
      params.name,
      params.currencyId,
    ],
    async () => {
      const response = await getListBankAccount(params);
      const totalCountFromHeader = Number(response.headers?.['x-total-count'] || 0);
      // Transform AxiosResponse to expected format
      if (response.data?.data) {
        return {
          data: response.data.data.items || response.data.data || [],
          totalCount:
            response.data.data.total ||
            response.data.data.totalCount ||
            totalCountFromHeader,
          page: response.data.data.page || 1,
          pageSize: response.data.data.pageSize || DEFAULT_BANK_ACCOUNT_PAGE_SIZE,
          totalPages: response.data.data.totalPages || 0,
        };
      }
      // Fallback if response structure is different
      return {
        data: Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.items)
            ? response.data.items
            : [],
        totalCount: totalCountFromHeader,
        page: 1,
        pageSize: DEFAULT_BANK_ACCOUNT_PAGE_SIZE,
        totalPages: 0,
      };
    },
    {
      enabled: params.enabled !== false && !!params.companyId,
    }
  )
}

export const useGetListBankAccountProvider = (params: {
  companyId: number
  start: number
  end: number
  name?: string
  currencyId?: number
}) => {
  return useQuery<{
    data: {
      data: {
        total: number
        page: number
        pageSize: number
        totalPages: number
        items: []
      }
    }
  }>(
    [
      `/api/v1/bank_accounts/get-list/provider`,
      params.companyId,
      params.start,
      params.end,
      params.name,
      params.currencyId,
    ],
    async () => getListBankAccount(params),
    {
      enabled: !!params.companyId,
    }
  )
}

export function getListBankAccount({
  companyId,
  start,
  end,
  name,
  currencyId,
}: {
  companyId?: number
  start: number
  end: number
  name?: string
  currencyId?: number
}) {
  const params = new URLSearchParams()
  params.append('_start', String(start))
  params.append('_end', String(end))
  if (name) params.append('_like', name)
  if (currencyId) params.append('_currencyId', String(currencyId))
  if (companyId) params.append('companyId', String(companyId))

  return httpClient.get(
    `${ROOT_URL}/api/v1/bank_accounts/non_default?${params.toString()}`
  )
}

// Keep old function for backward compatibility
export function getListBankAccounts({
  companyId,
  start,
  end,
  name,
}: {
  companyId: number;
  start: number;
  end: number;
  name?: string;
}) {
  return getListBankAccount({ companyId, start, end, name });
}

export const useGetListBankAccounts = (params: {
  companyId: number;
  start: number;
  end: number;
  name?: string;
}) => {
  return useGetListBankAccount(params);
};

export function getDetailBankAcccount(id: number | null) {
  return httpClient.get(`${ROOT_URL}/api/v1/bank_accounts/${id}`);
}

// Keep old function for backward compatibility
export function getDetailBankAccount(id: number | null) {
  return getDetailBankAcccount(id);
}

export const useGetDetailBankAcccount = (id: number | null) => {
  return useQuery<{ data: any }>(
    [`/api/v1/bank_accounts/detail/:id`, id],
    async () => {
      return getDetailBankAcccount(id);
    },
    {
      enabled: !!id,
    }
  );
};

export const useCreateBankAccount = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: createBankAccount,
  });
};

export function createBankAccount(bank: Store) {
  return httpClient.post(`${ROOT_URL}/api/v1/bank_accounts/create`, bank);
}

export const useUpdateBankAccount = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: updateBankAccount,
  });
};

export function updateBankAccount(bank: Store) {
  return httpClient.patch(`${ROOT_URL}/api/v1/bank_accounts/update`, bank);
}

export const useDeleteBankAccount = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: deleteBankAccount,
  });
};

export function deleteBankAccount(id: number | null) {
  return httpClient.delete(`${ROOT_URL}/api/v1/bank_accounts/delete/${id}`);
}

export function getUserBanks({
  start,
  end,
  name,
  userId,
}: {
  start: number;
  end: number;
  name?: string;
  userId?: number | null;
}) {
  const params = new URLSearchParams();
  params.append('_start', String(start));
  params.append('_end', String(end));
  if (name) params.append('_like', name);
  if (userId) params.append('_userId', String(userId));
  return httpClient.get(
    `${ROOT_URL}/api/v1/banks/users?${params.toString()}`
  );
}

export const useGetUserBank = (params: {
  start: number;
  end: number;
  name?: string;
  userId?: number | null;
  enabled?: boolean;
}) => {
  return useQuery<{ data: any[] }>(
    ['/api/v1/banks/users', params.start, params.end, params.name, params.userId],
    async () => getUserBanks(params),
    {
      enabled: params.enabled !== false && !!params.userId,
    }
  );
};

export function getSystemBanks({
  start,
  end,
  name,
}: {
  start: number;
  end: number;
  name?: string;
}) {
  const params = new URLSearchParams();
  params.append('_start', String(start));
  params.append('_end', String(end));
  if (name) params.append('_like', name);
  return httpClient.get(
    `${ROOT_URL}/api/v1/banks/system?${params.toString()}`
  );
}

export const useGetSystemBank = (params: {
  start: number;
  end: number;
  name?: string;
}) => {
  return useQuery<{ data: any[] }>(
    ['/api/v1/banks/system', params.start, params.end, params.name],
    async () => getSystemBanks(params)
  );
};

